import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useInvoices } from '@/hooks/useInvoices';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

import SuccessMessage from '../invoices/SuccessMessage';
import SearchHeader from '../invoices/SearchHeader';
import TransactionsTable from '../invoices/TransactionsTable';
import EmptyState from '../invoices/EmptyState';
import ReceiptModal from '../invoices/Receipt';
import Pagination from '../analysisHistory/Pagination';

const Invoices = () => {
    const [searchParams] = useSearchParams();
    const { toast } = useToast();
    const { user } = useAuth();
    const [recentTransaction, setRecentTransaction] = useState(null);
    const [localLoading, setLocalLoading] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 10;

    const {
        transactions,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        fetchRecentTransaction
    } = useInvoices(user?.id);

    const hasTransactions = transactions.length > 0;
    const hasRecentTransaction = recentTransaction !== null;
    const isLoading = loading || localLoading;

    useEffect(() => {
        const paymentStatus = searchParams.get('redirect_status');
        const paymentIntent = searchParams.get('payment_intent');

        if (paymentStatus === 'succeeded' && paymentIntent) {
            toast({
                title: "Payment Successful!",
                description: "Your payment has been processed and credits have been added to your account.",
                duration: 5000,
            });
            loadRecentTransaction(paymentIntent);
        }
    }, [searchParams, toast]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const loadRecentTransaction = async (sessionId) => {
        setLocalLoading(true);
        const transaction = await fetchRecentTransaction(sessionId);
        if (transaction) setRecentTransaction(transaction);
        setLocalLoading(false);
    };

    const getStatusColor = (amount) => {
        if (amount >= 1000) return 'text-purple-600 dark:text-purple-400';
        if (amount >= 500) return 'text-blue-600 dark:text-blue-400';
        return 'text-green-600 dark:text-green-400';
    };

    const getInvoiceId = (id) => {
        return `INV-${id.substring(0, 8).toUpperCase()}`;
    };

    const filteredTransactions = transactions.filter(transaction =>
        getInvoiceId(transaction.id).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination calculations
    const totalEntries = filteredTransactions.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + entriesPerPage);

    const showEmptyState = !isLoading && !hasTransactions && !hasRecentTransaction && !searchTerm;
    const showNoResults = !isLoading && filteredTransactions.length === 0 && searchTerm;

    const handleViewReceipt = (transaction) => {
        setSelectedTransaction(transaction);
        setIsReceiptOpen(true);
    };

    return (
        <div className="container mx-auto py-8 px-4 space-y-8">
            <SuccessMessage
                transaction={recentTransaction}
                getInvoiceId={getInvoiceId}
                onViewReceipt={handleViewReceipt}
            />

            <SearchHeader
                showSearch={!showEmptyState}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                isLoading={isLoading}
            />

            {error && (
                <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg mb-6" role="alert">
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            <Card>
                {showEmptyState ? (
                    <EmptyState />
                ) : showNoResults ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <p className="text-muted-foreground text-center">
                            No invoices found matching "{searchTerm}"
                        </p>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="overflow-x-auto">
                            <TransactionsTable
                                transactions={paginatedTransactions}
                                isLoading={isLoading}
                                getInvoiceId={getInvoiceId}
                                getStatusColor={getStatusColor}
                                onViewReceipt={handleViewReceipt}
                            />
                        </div>
                        {hasTransactions && (
                            <div className="pt-4">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    setCurrentPage={setCurrentPage}
                                    totalEntries={totalEntries}
                                    entriesPerPage={entriesPerPage}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Card>

            <ReceiptModal
                transaction={selectedTransaction}
                isOpen={isReceiptOpen}
                onClose={() => {
                    setIsReceiptOpen(false);
                    setSelectedTransaction(null);
                }}
            />
        </div>
    );
};

export default Invoices;